/*
  MONO landing — vanilla JS.
  Responsibilities:
    1) Force video autoplay (some browsers ignore the `autoplay` attribute).
    2) At half-time of the video, add `.is-dark` to <html> and update hero data-mode.
    3) After video ends, freeze on the still and stop the video tag.
    4) Color swatches: swap visible still per variant + update hidden cart input + accent color.
*/
(function () {
  'use strict';

  // Apply persisted day/night preference as early as possible — keeps the
  // toggle state across navigation between pages.
  //
  // Canonical key is `lase-theme` (also used by assets/theme-toggle.js),
  // canonical class is `is-dark`. Legacy `mono-theme` + `mono-dark` are
  // migrated on first read so users who set their preference before the
  // rename don't snap back to light mode.
  const THEME_KEY        = 'lase-theme';
  const LEGACY_THEME_KEY = 'mono-theme';
  try {
    let pref = localStorage.getItem(THEME_KEY);
    if (!pref) {
      const legacy = localStorage.getItem(LEGACY_THEME_KEY);
      if (legacy) {
        pref = legacy;
        localStorage.setItem(THEME_KEY, legacy);
        localStorage.removeItem(LEGACY_THEME_KEY);
      }
    }
    // Dark mode is currently paused everywhere except the 3D
    // configurator (window.__LASE_DARK_ALLOWED, set in theme.liquid).
    // Only re-apply the persisted `night` preference where it's allowed
    // — the stored value is left intact so the configurator still
    // remembers it.
    if (pref === 'night' && window.__LASE_DARK_ALLOWED !== false) {
      document.documentElement.classList.add('is-dark');
    }
  } catch (e) { /* localStorage unavailable */ }

  // Built-in name → hex map (used until store sets `metafields.mono.color_hex`)
  const COLOR_MAP = {
    sage:     '#6e8a6f', green:   '#6e8a6f', moss:    '#6e8a6f',
    ochre:    '#c9a449', yellow:  '#c9a449', marigold:'#c9a449', sun: '#c9a449',
    slate:    '#7593c2', cobalt:  '#7593c2', blue:    '#7593c2',
    terra:    '#b85547', red:     '#b85547', brick:   '#b85547',
    ember:    '#d76b3a', orange:  '#d76b3a',
    rose:     '#d18cae', pink:    '#d18cae',
    lavender: '#a89bd6', purple:  '#a89bd6',
    bone:     '#d8d2c4', cream:   '#d8d2c4',
  };

  function hexFromName (name) {
    if (!name) return '#888';
    const k = String(name).trim().toLowerCase();
    return COLOR_MAP[k] || '#888';
  }

  function fillSwatchHex (root) {
    root.querySelectorAll('[data-mono-swatch]').forEach((el) => {
      // If theme already provided a hex from a metafield, keep it
      const current = el.style.getPropertyValue('--swatch').trim();
      if (current && current !== '#999') return;
      const label = el.querySelector('.visually-hidden');
      el.style.setProperty('--swatch', hexFromName(label && label.textContent));
    });
  }

  // ---------- HERO video & day/night ----------
  // Track all hero elements so the global toggle can update them.
  const heroRoots = new Set();

  function setMode (mode) {
    // Dark mode paused outside the configurator: force light and DON'T
    // persist, so a homepage hero video reaching its night half-time
    // can't darken the storefront — nor clobber the stored preference
    // the configurator relies on.
    if (window.__LASE_DARK_ALLOWED === false) {
      document.documentElement.classList.remove('is-dark');
      heroRoots.forEach((r) => r.setAttribute('data-mono-mode', 'day'));
      return;
    }
    if (mode === 'night') {
      document.documentElement.classList.add('is-dark');
    } else {
      document.documentElement.classList.remove('is-dark');
    }
    try { localStorage.setItem(THEME_KEY, mode); } catch (e) { /* noop */ }
    heroRoots.forEach((r) => r.setAttribute('data-mono-mode', mode));
  }

  function setupHero (root) {
    heroRoots.add(root);
    // Default visible mode (used by the still selector once phase=ended)
    if (!root.getAttribute('data-mono-mode')) {
      root.setAttribute('data-mono-mode', 'day');
    }

    const video = root.querySelector('[data-mono-hero-video]');
    if (!video) {
      // No video on page — show stills immediately so we still see something
      root.setAttribute('data-mono-phase', 'ended');
      return;
    }

    let switched = false;
    const switchToNight = () => {
      if (switched) return;
      switched = true;
      setMode('night');
    };

    video.addEventListener('timeupdate', () => {
      const d = video.duration;
      if (!isFinite(d) || d === 0) return;
      if (video.currentTime >= d / 2) switchToNight();
    });

    video.addEventListener('ended', () => {
      root.setAttribute('data-mono-phase', 'ended');
      if (!switched) switchToNight();
    });

    // Fail-safe: if video can't play within a few seconds, show the still anyway
    const fallbackTimer = setTimeout(() => {
      if (video.readyState < 2 || video.paused) {
        root.setAttribute('data-mono-phase', 'ended');
      }
    }, 4000);
    video.addEventListener('playing', () => clearTimeout(fallbackTimer), { once: true });

    // Force play (autoplay attribute alone is sometimes ignored)
    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    video.load();
    tryPlay();
    video.addEventListener('canplay', tryPlay, { once: true });
  }

  // ---------- Floating day/night toggle ----------
  // The global toggle (snippets/theme-toggle + assets/theme-toggle.js)
  // owns the click + class flip. We only sync hero state here by watching
  // <html class="is-dark"> with a MutationObserver — that avoids the
  // double-bind race that was causing the day/night still to invert
  // every other click.
  function syncHeroesToTheme () {
    const dark = document.documentElement.classList.contains('is-dark');
    const mode = dark ? 'night' : 'day';
    heroRoots.forEach((r) => {
      r.setAttribute('data-mono-mode', mode);
      // Once the user takes control we always show a still rather than
      // jumping back to the playing-video phase.
      r.setAttribute('data-mono-phase', 'ended');
    });
    // Keep localStorage in sync if something else changed the class
    try { localStorage.setItem(THEME_KEY, mode); } catch (e) {}
  }

  function watchHtmlClass () {
    if (watchHtmlClass._started) return;
    watchHtmlClass._started = true;
    new MutationObserver(syncHeroesToTheme).observe(document.documentElement, {
      attributes: true, attributeFilter: ['class']
    });
  }

  // ---------- BUY: swatch → variant preview + product link ----------
  function setupBuy (root) {
    const wrap = root.querySelector('[data-mono-buy]');
    if (!wrap) return;

    const link = wrap.querySelector('[data-mono-buy-link]');
    const currentColorLabel = wrap.querySelector('[data-mono-current-color]');
    const priceEl = root.querySelector('[data-mono-price]');
    const swatches = wrap.querySelectorAll('[data-mono-swatch]');

    function apply (swatch) {
      const accent = swatch.style.getPropertyValue('--swatch').trim();
      const label = swatch.querySelector('.visually-hidden');
      const variantImage = swatch.getAttribute('data-variant-image');
      const variantUrl = swatch.getAttribute('data-variant-url');
      const variantPrice = swatch.getAttribute('data-variant-price');

      if (accent) document.documentElement.style.setProperty('--mono-accent', accent);
      if (currentColorLabel && label) currentColorLabel.textContent = label.textContent;
      if (link && variantUrl) link.href = variantUrl;
      if (priceEl && variantPrice) priceEl.textContent = variantPrice;

      // Swap hero stills if a variant image is provided
      if (variantImage) {
        const dayStill = document.querySelector('[data-mono-hero-still="day"]');
        if (dayStill) dayStill.src = variantImage;
        // Night still keeps showing the lit-up shot — could be a separate metafield later
      }
    }

    swatches.forEach((sw) => {
      const input = sw.querySelector('[data-mono-color-input]');
      sw.addEventListener('click', () => {
        if (input) input.checked = true;
        apply(sw);
      });
      if (input && input.checked) apply(sw);
    });
  }

  // ---------- PDP: horizontal variant gallery + size/color pickers + floating bar ----------
  function setupPDP (root) {
    if (root.dataset.monoPdpBound) return;
    root.dataset.monoPdpBound = '1';

    // Variant data baked into the section as JSON
    const dataNode = root.querySelector('[data-mono-pdp-variants]');
    let variants = [];
    if (dataNode) {
      try { variants = JSON.parse(dataNode.textContent); }
      catch (e) { console.warn('mono-pdp: variant JSON parse failed', e); }
    }

    // Option indexes are now name-aware (Liquid maps Color → swatches,
    // Size → text, etc. regardless of the admin-side position). Read
    // them off the section so the JS doesn't have to guess. -1 means
    // the product doesn't have that option at all.
    const COLOR_IDX = Number(dataNode?.dataset?.colorIdx ?? -1);
    const SIZE_IDX  = Number(dataNode?.dataset?.sizeIdx  ?? -1);
    const THIRD_IDX = Number(dataNode?.dataset?.thirdIdx ?? -1);

    const form         = root.querySelector('[data-mono-pdp-form]');
    const variantInput = root.querySelector('[data-mono-pdp-variant]');
    const priceEl      = root.querySelector('[data-mono-pdp-price]');
    const colorLabel   = root.querySelector('[data-mono-pdp-current-color]');
    const sizeLabel    = root.querySelector('[data-mono-pdp-current-size]');
    const setLabel     = root.querySelector('[data-mono-pdp-current-set]');
    const gallery      = root.querySelector('[data-mono-pdp-gallery]');
    const track        = root.querySelector('[data-mono-pdp-track]');
    const prevBtn      = root.querySelector('[data-mono-pdp-prev]');
    const nextBtn      = root.querySelector('[data-mono-pdp-next]');
    const swatches     = root.querySelectorAll('[data-mono-pdp-swatch]');
    const sizeBtns     = root.querySelectorAll('[data-mono-pdp-size]');
    const setBtns      = root.querySelectorAll('[data-mono-pdp-set]');
    const cta          = root.querySelector('[data-mono-pdp-cta]');
    const customizeBtn = root.querySelector('[data-mono-pdp-customize]');
    const bar          = root.querySelector('[data-mono-pdp-bar]');
    const barImage     = root.querySelector('[data-mono-pdp-bar-image]');
    const barPrice     = root.querySelector('[data-mono-pdp-bar-price]');
    const barCta       = root.querySelector('[data-mono-pdp-bar-cta]');

    // Part-variant lookup for the "What's in this design" carousel —
    // resolves the variant's preset_hash to real part product cards.
    // These live just AFTER the hero section (outside its 100vh overflow
    // clip), so query the document — one PDP per page.
    let PARTS = {};
    const partsNode = document.querySelector('[data-mono-parts]');
    if (partsNode) { try { PARTS = JSON.parse(partsNode.textContent); } catch (e) { /* keep {} */ } }
    const componentsHost = document.querySelector('[data-mono-components]');
    const componentsSection = document.querySelector('[data-mono-parts-section]');
    const partsCustomize = document.querySelector('[data-mono-parts-customize]');
    const partsNav  = document.querySelector('[data-mono-parts-nav]');
    const partsPrev = document.querySelector('[data-mono-parts-prev]');
    const partsNext = document.querySelector('[data-mono-parts-next]');

    // Show/hide the carousel arrows depending on whether the track
    // actually overflows (few parts fill the row and need no scroller;
    // many parts overflow → arrows appear, like the homepage sliders).
    function updatePartsNav () {
      if (!componentsHost || !partsNav) return;
      const overflow = componentsHost.scrollWidth - componentsHost.clientWidth > 2;
      partsNav.hidden = !overflow;
      if (!overflow) return;
      const max = componentsHost.scrollWidth - componentsHost.clientWidth - 1;
      if (partsPrev) partsPrev.disabled = componentsHost.scrollLeft <= 0;
      if (partsNext) partsNext.disabled = componentsHost.scrollLeft >= max;
    }
    const partsStep = () => {
      const first = componentsHost && componentsHost.querySelector('.mono-parts__card');
      if (!first) return 300;
      const gap = parseInt(getComputedStyle(componentsHost).columnGap || getComputedStyle(componentsHost).gap || '0', 10) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    if (partsPrev) partsPrev.addEventListener('click', () => componentsHost.scrollBy({ left: -partsStep(), behavior: 'smooth' }));
    if (partsNext) partsNext.addEventListener('click', () => componentsHost.scrollBy({ left: partsStep(), behavior: 'smooth' }));
    if (componentsHost) componentsHost.addEventListener('scroll', updatePartsNav, { passive: true });
    window.addEventListener('resize', updatePartsNav);

    // Floating-pill nav links: Overview = the very top (the photos),
    // the rest smooth-scroll to their anchored sections. Active link
    // follows the scroll position.
    const barNav = root.querySelector('[data-mono-pdp-bar-nav]');
    if (barNav) {
      const links = Array.from(barNav.querySelectorAll('.mono-pdp-bar__link'));
      const targets = links.map((l) => {
        const href = l.getAttribute('href');
        return href === '#top' ? null : document.getElementById(href.slice(1));
      });
      links.forEach((l, i) => {
        l.addEventListener('click', (e) => {
          e.preventDefault();
          if (targets[i] === null) window.scrollTo({ top: 0, behavior: 'smooth' });
          else if (targets[i]) targets[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      let navTick = false;
      const navUpdate = () => {
        navTick = false;
        const line = window.innerHeight * 0.4;
        let idx = 0;
        targets.forEach((t, i) => {
          if (t && t.getBoundingClientRect().top <= line) idx = i;
        });
        links.forEach((l, i) => l.classList.toggle('is-active', i === idx));
      };
      window.addEventListener('scroll', () => {
        if (!navTick) { navTick = true; requestAnimationFrame(navUpdate); }
      }, { passive: true });
    }

    // Inline "Read more…" appended to the end of the intro's last
    // paragraph (smaller, quiet) — scrolls to the story sections that
    // sit below the hero (the Shopify section wrapper's next sibling).
    const intro = root.querySelector('.mono-pdp__intro');
    if (intro && !(intro.nextElementSibling && intro.nextElementSibling.classList.contains('mono-pdp__readmore'))) {
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'mono-pdp__readmore';
      rm.textContent = 'Read more';
      // Insert as a sibling right AFTER the intro so it always sits on
      // its own line below the paragraph, flush-left with the copy.
      intro.parentNode.insertBefore(rm, intro.nextSibling);
      rm.addEventListener('click', () => {
        const wrap = root.closest('.shopify-section') || root;
        const next = wrap.nextElementSibling;
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Build the components carousel from a variant's preset_hash:
    //   v1!<modules>!<connectors>!<light>!<view>
    // modules "L-P01-matte-green" → key "matte-l-green" in PARTS.modules.
    // Curated designs always include the glass vase insert.
    function renderComponents (v) {
      if (!componentsHost || !componentsSection) return;
      const hash = v && v.preset_hash ? String(v.preset_hash) : '';
      if (!hash) { componentsSection.setAttribute('hidden', ''); return; }
      const seg = hash.split('!'); // [v1, modules, connectors, light, view]
      const items = [];
      (seg[1] || '').split(',').map(s => s.trim()).filter(Boolean).forEach(tok => {
        const t = tok.toLowerCase().split('-'); // [size, pattern, finish, color]
        if (t.length < 4) return;
        const m = PARTS.modules && PARTS.modules[t[2] + '-' + t[0] + '-' + t[3]];
        if (m) items.push(m);
      });
      (seg[2] || '').split(',').map(s => s.trim()).filter(Boolean).forEach(c => {
        const cc = PARTS.connectors && PARTS.connectors[c.toLowerCase()];
        if (cc) items.push(cc);
      });
      if (PARTS.glass) items.push(PARTS.glass);

      // Merge identical parts into a qty.
      const agg = [], seen = {};
      items.forEach(it => {
        const k = it.url;
        if (seen[k] != null) agg[seen[k]].qty++;
        else { seen[k] = agg.length; agg.push({ ...it, qty: 1 }); }
      });
      if (!agg.length) { componentsSection.setAttribute('hidden', ''); return; }

      const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      componentsHost.innerHTML = agg.map(it => (
        '<a class="mono-parts__card" href="' + esc(it.url) + '">' +
          '<div class="mono-parts__media">' +
            (it.img ? '<img src="' + esc(it.img) + '" alt="" loading="lazy">' : '') +
            // Dark shot crossfades in on hover — same "lights on"
            // moment as the Parts catalogue cards.
            (it.night ? '<img class="mono-parts__night" src="' + esc(it.night) + '" alt="" aria-hidden="true" loading="lazy">' : '') +
          '</div>' +
          '<div class="mono-parts__info">' +
            '<span class="mono-parts__title">' + (it.qty > 1 ? it.qty + '× ' : '') + esc(it.title) + '</span>' +
            (it.sub ? '<span class="mono-parts__sub">' + esc(it.sub) + '</span>' : '') +
          '</div>' +
        '</a>'
      )).join('');
      componentsSection.removeAttribute('hidden');
      componentsHost.scrollLeft = 0;
      updatePartsNav();
      requestAnimationFrame(updatePartsNav);
    }

    // Initial variant from the hidden input
    let currentVariant = null;
    if (variantInput && variantInput.value) {
      currentVariant = variants.find(v => String(v.id) === String(variantInput.value));
    }
    if (!currentVariant && variants.length) currentVariant = variants[0];

    // Generic option matcher — accepts an array of option values
    // (in product.options order) and returns the best matching variant.
    // Falls back progressively by dropping trailing options if no exact
    // match exists (e.g. when a colour+size combo isn't stocked for
    // every set variant).
    function findVariant (opts) {
      if (!Array.isArray(opts)) return null;
      // Try exact match across all specified options
      let v = variants.find(x => x.options.every((o, i) => opts[i] == null || o === opts[i]));
      if (v) return v;
      // Drop trailing options one by one until we find something
      for (let depth = opts.length - 1; depth > 0; depth--) {
        v = variants.find(x => {
          for (let i = 0; i < depth; i++) {
            if (opts[i] != null && x.options[i] !== opts[i]) return false;
          }
          return true;
        });
        if (v) return v;
      }
      return null;
    }

    function setGallery (images) {
      if (!track || !Array.isArray(images)) return;
      const currentSrcs = Array.from(track.querySelectorAll('img')).slice(0, loopOriginalCount() || undefined).map(i => i.getAttribute('src'));
      const same = currentSrcs.length === images.length &&
                   currentSrcs.every((s, i) => s === images[i]);
      if (same) return;

      while (track.firstChild) track.removeChild(track.firstChild);
      images.forEach((url, i) => {
        const slide = document.createElement('div');
        slide.className = 'mono-pdp__slide';
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        // Eager on purpose: the looping gallery jumps scrollLeft
        // instantly, which permanently defers Chrome's lazy loader
        // (night slides never painted). ≤7 unique images per PDP.
        img.loading = 'eager';
        slide.appendChild(img);
        track.appendChild(slide);
      });
      ensureLoopClones();
      if (gallery) jumpInstant(loopSetWidth());
      rebuildPagination();
    }

    // ----- Infinite gallery loop ------------------------------------
    // One full clone set is appended after the originals. Whenever the
    // scroll position enters the clone set, we instantly shift back by
    // one set width — the frames are pixel-identical, so the sequence
    // repeats seamlessly and the last photo can travel past the buy card.
    function loopOriginalCount () {
      if (!track) return 0;
      const n = Number(track.dataset.monoLoopCount || 0);
      return n || track.children.length;
    }
    function loopSetWidth () {
      if (!track) return 0;
      const n = loopOriginalCount();
      const kids = track.children;
      if (!n || kids.length <= n) return 0; // no clones (single image)
      return kids[n].offsetLeft - kids[0].offsetLeft;
    }
    function jumpInstant (left) {
      if (!gallery) return left;
      const prev = gallery.style.scrollBehavior;
      gallery.style.scrollBehavior = 'auto';
      gallery.scrollLeft = left;
      gallery.style.scrollBehavior = prev;
      return left;
    }
    function ensureLoopClones () {
      if (!track) return;
      Array.from(track.querySelectorAll('[data-mono-clone]')).forEach(n => n.remove());
      const originals = Array.from(track.children);
      track.dataset.monoLoopCount = String(originals.length);
      if (originals.length <= 1) return;
      const mk = (slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-mono-clone', '1');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('img').forEach(i => { i.loading = 'eager'; });
        return clone;
      };
      // Full clone set on BOTH sides: [A clones][originals][B clones] —
      // the window starts on the originals, so there is always a set's
      // worth of runway in each direction and swiping loops both ways.
      for (let i = originals.length - 1; i >= 0; i--) track.insertBefore(mk(originals[i]), track.firstChild);
      originals.forEach((slide) => track.appendChild(mk(slide)));
    }

    function rebuildPagination () {
      if (!pagination || !track) return;
      while (pagination.firstChild) pagination.removeChild(pagination.firstChild);
      const n = loopOriginalCount();
      const kids = Array.from(track.children);
      const slides = kids.length > n ? kids.slice(n, n + n) : kids;
      if (slides.length <= 1) return; // hide indicator for single image
      slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mono-pdp__page-dot';
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          slide.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
        });
        pagination.appendChild(dot);
      });
      updatePagination();
    }

    function updatePagination () {
      if (!pagination) return;
      const dots = pagination.querySelectorAll('.mono-pdp__page-dot');
      if (!dots.length) return;
      const idx = currentSlideIndex();
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
    }

    // ----- Gallery image picker
    //
    // Each variant carries two image lists (day_images + night_images).
    // The page-level dark mode is retired, so the PDP gallery shows ONE
    // unified set: all day shots followed by all night shots. (If dark
    // mode is ever active, night leads instead.) Legacy `images` field
    // (pre-day/night refactor) is honoured when both lists are empty.
    function pickModeImages (v) {
      if (!v) return [];
      const isDark  = document.documentElement.classList.contains('is-dark');
      const day     = Array.isArray(v.day_images)   ? v.day_images   : [];
      const night   = Array.isArray(v.night_images) ? v.night_images : [];
      const legacy  = Array.isArray(v.images)       ? v.images       : [];
      const all     = isDark ? night.concat(day) : day.concat(night);
      return all.length ? all : legacy;
    }

    // Re-apply current variant when the user toggles light/dark, so
    // the gallery + floating bar thumb + lightbox all swap together.
    // The MutationObserver in watchHtmlClass() fires before paint
    // when html.is-dark flips, which lets setGallery do its smooth
    // crossfade transition (CSS `opacity` on .mono-pdp__slide).
    const themeObserver = new MutationObserver(() => {
      if (currentVariant) applyVariant(currentVariant);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Update each colour swatch's thumbnail to the image of the variant
    // matching the CURRENTLY SELECTED size (+ current day/night mode).
    // So switching S → L re-renders every swatch preview with the L
    // photo, and toggling dark mode swaps them to night shots. Only
    // touches swatches that already have an <img> (image swatches); a
    // hex-dot swatch (no per-variant photo) is left untouched, so MONO
    // and any image-less product are unaffected.
    function pickThumb (v) {
      if (!v) return '';
      const isDark = document.documentElement.classList.contains('is-dark');
      const day    = v.thumb_day   || '';
      const night  = v.thumb_night || '';
      return isDark ? (night || day) : (day || night);
    }
    function updateSwatchImages () {
      if (COLOR_IDX < 0 || !currentVariant) return;
      swatches.forEach((sw) => {
        const img = sw.querySelector('.mono-pdp__swatch-img');
        if (!img) return;
        const opts = currentVariant.options.slice();
        opts[COLOR_IDX] = sw.dataset.color;
        const v = findVariant(opts);
        if (!v) return;
        const thumb = pickThumb(v);
        if (thumb && img.getAttribute('src') !== thumb) {
          img.src = thumb;
        }
      });
    }

    function applyVariant (v, opts) {
      if (!v) return;
      currentVariant = v;
      opts = opts || {};

      const colorVal = COLOR_IDX >= 0 ? (v.options[COLOR_IDX] || '') : '';
      const sizeVal  = SIZE_IDX  >= 0 ? (v.options[SIZE_IDX]  || '') : '';
      const thirdVal = THIRD_IDX >= 0 ? (v.options[THIRD_IDX] || '') : '';

      if (variantInput) variantInput.value = v.id;
      if (priceEl)   priceEl.textContent  = v.price;
      if (barPrice)  barPrice.textContent = v.price;
      if (colorLabel) colorLabel.textContent = colorVal;
      if (sizeLabel && sizeVal)  sizeLabel.textContent = sizeVal;
      if (setLabel  && thirdVal) setLabel.textContent  = thirdVal;

      // Accent from the matching swatch
      const colorAttr = colorVal.replace(/"/g, '\\"');
      const colorSwatch = colorAttr
        ? root.querySelector('[data-mono-pdp-swatch][data-color="' + colorAttr + '"]')
        : null;
      if (colorSwatch) {
        const accent = colorSwatch.style.getPropertyValue('--swatch').trim();
        if (accent) document.documentElement.style.setProperty('--mono-accent', accent);
        const input = colorSwatch.querySelector('input[type="radio"]');
        if (input) input.checked = true;
      }

      // Mark active size pill
      if (sizeVal) {
        sizeBtns.forEach(b => b.classList.toggle('is-active', b.dataset.size === sizeVal));
      }
      // Mark active third-option pill
      if (thirdVal) {
        setBtns.forEach(b => b.classList.toggle('is-active', b.dataset.set === thirdVal));
      }

      // Always refresh gallery + lightbox + bar thumb to the variant's
      // image set. The day/night photo system: each variant carries
      // day_images + night_images arrays; pickModeImages chooses the
      // right list for the current html.is-dark state with a graceful
      // fall back if one side is empty.
      const imgs = pickModeImages(v);
      setGallery(imgs);
      if (barImage && imgs.length) barImage.src = imgs[0];
      if (typeof root.__monoPdpSetLightboxImages === 'function') {
        root.__monoPdpSetLightboxImages(imgs);
      }

      // Refresh the "Customize" deeplink to point at this variant's
      // configurator preset. The link is rendered server-side for the
      // first variant; subsequent variant picks need this update so
      // clicking Customize lands on the configurator with the matching
      // stack (e.g. MONO L Pink → 3 modules + 2 connectors in pink at
      // L middle / S top + bottom). preset_hash is a flat v1-format
      // string that the configurator's decodeState understands as the
      // URL hash, so we just splice it onto a `#c=` suffix.
      // Any section can opt into the live configurator deeplink by
      // carrying [data-mono-customize-link] (e.g. S5 "Build yours").
      const extraCustomize = Array.from(document.querySelectorAll('[data-mono-customize-link]'));
      [customizeBtn, partsCustomize].concat(extraCustomize).forEach((btn) => {
        if (!btn) return;
        const base = btn.dataset.customizeBase || '/pages/customize';
        const handle = btn.dataset.productHandle || '';
        const qs = '?from=' + encodeURIComponent(handle) + '&variant=' + v.id;
        const hash = v.preset_hash ? ('#c=' + v.preset_hash) : '';
        btn.href = base + qs + hash;
      });

      // Re-sync the colour-picker thumbnails to the now-selected size
      // (+ day/night mode). Runs on every variant change including the
      // initial paint and theme toggle.
      updateSwatchImages();

      // Banner rides the variant's night shot when it runs in
      // night-fallback mode (no admin-picked image).
      const bannerImg = document.querySelector('[data-mono-pdp-banner-img]');
      if (bannerImg && Array.isArray(v.night_images) && v.night_images[0]) {
        bannerImg.src = v.night_images[0];
      }

      // Dimmer photos follow the colour too.
      const dimDay   = document.querySelector('[data-mono-dimmer-day]');
      const dimNight = document.querySelector('[data-mono-dimmer-night]');
      if (dimDay && Array.isArray(v.day_images) && v.day_images[0]) dimDay.src = v.day_images[0];
      if (dimNight && Array.isArray(v.night_images) && v.night_images[0]) dimNight.src = v.night_images[0];

      // Rebuild the "What's in this design" carousel for this variant.
      renderComponents(v);
    }

    // Any picker click → switch to the matching variant and refresh
    // gallery + lightbox if the image set differs (setGallery short-
    // circuits when the URLs are identical, so no flicker on size
    // changes that share images).
    swatches.forEach((sw) => {
      sw.addEventListener('click', () => {
        if (COLOR_IDX < 0) return;
        const opts = currentVariant ? currentVariant.options.slice() : [];
        opts[COLOR_IDX] = sw.dataset.color;
        applyVariant(findVariant(opts));
      });
    });
    sizeBtns.forEach((b) => {
      b.addEventListener('click', () => {
        if (SIZE_IDX < 0) return;
        const opts = currentVariant ? currentVariant.options.slice() : [];
        opts[SIZE_IDX] = b.dataset.size;
        applyVariant(findVariant(opts));
      });
    });
    setBtns.forEach((b) => {
      b.addEventListener('click', () => {
        if (THIRD_IDX < 0) return;
        const opts = currentVariant ? currentVariant.options.slice() : [];
        opts[THIRD_IDX] = b.dataset.set;
        applyVariant(findVariant(opts));
      });
    });

    // Gallery navigation — click anywhere to advance, cursor follower on hover
    const cursor      = root.querySelector('[data-mono-pdp-cursor]');
    const cursorArrow = root.querySelector('[data-mono-pdp-cursor-arrow]');
    const zoomBtn     = root.querySelector('[data-mono-pdp-zoom]');
    const pagination  = root.querySelector('[data-mono-pdp-pagination]');
    const lightbox    = root.querySelector('[data-mono-pdp-lightbox]');
    const lbImg       = root.querySelector('[data-mono-pdp-lightbox-img]');
    const lbPrev      = root.querySelector('[data-mono-pdp-lightbox-prev]');
    const lbNext      = root.querySelector('[data-mono-pdp-lightbox-next]');
    const lbClose     = root.querySelector('[data-mono-pdp-lightbox-close]');
    const lbCounter   = root.querySelector('[data-mono-pdp-lightbox-counter]');

    // Keep a live reference to the current variant's images for the lightbox
    let lightboxImages = currentVariant && currentVariant.images ? currentVariant.images.slice() : [];
    let lightboxIndex  = 0;

    const finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

    let scrollByOne = null;
    if (gallery) {
      scrollByOne = (dir) => {
        const slides = track ? Array.from(track.children) : [];
        if (!slides.length || !gallery) return;
        const slideWidth = slides[0].offsetWidth;
        if (!slideWidth) return;
        const setW = loopSetWidth();
        let cur = gallery.scrollLeft;
        if (setW > 0) {
          // Teleport to the identical frame one set over BEFORE stepping,
          // so the smooth step itself never crosses a normalization seam.
          if (dir < 0 && cur < setW) cur = jumpInstant(cur + setW);
          if (dir > 0 && cur >= setW * 2 - slideWidth) cur = jumpInstant(cur - setW);
        }
        gallery.scrollTo({ left: dir > 0 ? cur + slideWidth : cur - slideWidth, behavior: 'smooth' });
      };
      if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); scrollByOne(-1); });
      if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); scrollByOne(1); });

      // Click-to-advance is desktop-only — on touch, swipe is the primary gesture
      // and a tap shouldn't accidentally scroll the gallery.
      if (finePointer) {
        gallery.addEventListener('click', (e) => {
          if (e.target.closest('button')) return;
          const rect = gallery.getBoundingClientRect();
          const x = e.clientX - rect.left;
          scrollByOne(x < rect.width / 2 ? -1 : 1);
        });
      }

      // Update pagination indicator on scroll (swipe, click-to-advance, button)
      let scrollRaf = null;
      gallery.addEventListener('scroll', () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => {
          const setW = loopSetWidth();
          if (setW > 0) {
            const p = gallery.scrollLeft;
            const slideW = track && track.children[0] ? track.children[0].offsetWidth : 0;
            if (p >= setW * 2) jumpInstant(p - setW);
            else if (p < (slideW || setW / 4) * 0.5) jumpInstant(p + setW);
          }
          updatePagination();
        });
      }, { passive: true });

      // Server-rendered slides need their clone sets too; start the
      // window on the originals so both directions have runway.
      ensureLoopClones();
      jumpInstant(loopSetWidth());
    }

    // Cursor follower — pointer:fine only, position:fixed in viewport
    if (gallery && cursor && finePointer) {
      gallery.addEventListener('mousemove', (e) => {
        // Use viewport coordinates (no scroll offset) — cursor is position:fixed
        cursor.style.transform = 'translate3d(' + e.clientX + 'px, ' + e.clientY + 'px, 0)';
        cursor.classList.add('is-visible');
        const rect = gallery.getBoundingClientRect();
        const isLeft = (e.clientX - rect.left) < rect.width / 2;
        if (cursorArrow) cursorArrow.textContent = isLeft ? '←' : '→';
      });
      gallery.addEventListener('mouseleave', () => {
        cursor.classList.remove('is-visible');
      });
    }

    // ---- Lightbox ----
    function currentSlideIndex () {
      if (!track || !gallery) return 0;
      const slides = Array.from(track.children);
      if (!slides.length) return 0;
      // Scroll-position based: use the leftmost slide that intersects the viewport.
      // This matches the new fixed-width-per-breakpoint slide layout.
      const slideWidth = slides[0].offsetWidth || gallery.clientWidth;
      const n = loopOriginalCount() || slides.length;
      const idx = Math.round(gallery.scrollLeft / slideWidth) % n;
      return Math.max(0, Math.min(idx, n - 1));
    }

    function paintLightbox () {
      if (!lbImg || !lightboxImages.length) return;
      lightboxIndex = Math.max(0, Math.min(lightboxIndex, lightboxImages.length - 1));
      lbImg.src = lightboxImages[lightboxIndex];
      if (lbCounter) {
        lbCounter.textContent = (lightboxIndex + 1) + ' / ' + lightboxImages.length;
      }
      resetLightboxZoom();
    }

    // ---- Lightbox zoom ----
    // Click/tap the photo toggles a 2.4× zoom anchored at the pointer;
    // while zoomed, moving the pointer (mouse or finger) pans — the
    // pointer position maps to the transform-origin, so sweeping across
    // the viewport sweeps across the whole photo. Any slide change or
    // close resets to fit.
    let lbZoomed = false;

    function setLightboxOrigin (clientX, clientY) {
      if (!lbImg) return;
      const r = lbImg.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((clientX - r.left) / r.width)  * 100));
      const y = Math.max(0, Math.min(100, ((clientY - r.top)  / r.height) * 100));
      lbImg.style.transformOrigin = x + '% ' + y + '%';
    }

    function resetLightboxZoom () {
      lbZoomed = false;
      if (lbImg) lbImg.style.transform = '';
      if (lightbox) lightbox.classList.remove('is-zoomed');
    }

    function toggleLightboxZoom (clientX, clientY) {
      if (!lbImg) return;
      if (lbZoomed) { resetLightboxZoom(); return; }
      setLightboxOrigin(clientX, clientY);
      lbImg.style.transform = 'scale(2.4)';
      lbZoomed = true;
      if (lightbox) lightbox.classList.add('is-zoomed');
    }

    if (lbImg) {
      lbImg.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleLightboxZoom(e.clientX, e.clientY);
      });
      lbImg.addEventListener('pointermove', (e) => {
        if (lbZoomed) setLightboxOrigin(e.clientX, e.clientY);
      });
    }

    function openLightbox (index) {
      if (!lightbox || !lightboxImages.length) return;
      lightboxIndex = index != null ? index : currentSlideIndex();
      paintLightbox();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Hide cursor follower while lightbox is open
      if (cursor) cursor.classList.remove('is-visible');
    }

    function closeLightbox () {
      if (!lightbox) return;
      resetLightboxZoom();
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function stepLightbox (dir) {
      if (!lightboxImages.length) return;
      lightboxIndex = (lightboxIndex + dir + lightboxImages.length) % lightboxImages.length;
      paintLightbox();
    }

    if (zoomBtn) zoomBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openLightbox();
    });
    if (lbPrev)  lbPrev.addEventListener('click', () => stepLightbox(-1));
    if (lbNext)  lbNext.addEventListener('click', () => stepLightbox(1));
    if (lbClose) lbClose.addEventListener('click', closeLightbox);
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        // Close when clicking backdrop (not on image/buttons)
        if (e.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener('keydown', (e) => {
      if (!lightbox || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  stepLightbox(-1);
      if (e.key === 'ArrowRight') stepLightbox(1);
    });

    // Expose so applyVariant can refresh images when variant changes
    root.__monoPdpSetLightboxImages = (images) => {
      lightboxImages = Array.isArray(images) ? images.slice() : [];
      lightboxIndex = 0;
    };

    // Floating bar — slide in once main CTA leaves the viewport
    if (bar && cta && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            bar.classList.remove('is-visible');
            bar.setAttribute('aria-hidden', 'true');
          } else {
            bar.classList.add('is-visible');
            bar.setAttribute('aria-hidden', 'false');
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      io.observe(cta);
    }

    // Bar CTA submits the real form
    if (barCta && form) {
      barCta.addEventListener('click', () => {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    }

    // Initial paint (sync UI to current variant)
    if (currentVariant) applyVariant(currentVariant, { changeGallery: false });
    // Build pagination dots for the server-rendered slides
    rebuildPagination();
  }

  // ---------- Init ----------
  function init () {
    document.querySelectorAll('[data-mono-hero]').forEach(setupHero);
    document.querySelectorAll('.mono-buy').forEach((root) => {
      fillSwatchHex(root);
      setupBuy(root);
    });
    document.querySelectorAll('[data-mono-pdp]').forEach(setupPDP);
    watchHtmlClass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify Theme Editor section reloads
  document.addEventListener('shopify:section:load', init);
})();
